import React, { useState, useEffect, useCallback, useRef } from 'react';
import './WebDevProgressNavigator.css';

const SECTIONS = [
  { id: 'far-web',         label: 'Fixed Asset' },
  { id: 'pr-web',          label: 'Purchase Request' },
  { id: 'team360-web',     label: 'Team 360' },
  { id: 'logistic-web',    label: 'Logistics' },
  { id: 'e-ware-web',      label: 'e-Warehouse' },
  { id: '1m2m-web',        label: '1M2M Portal' },
  { id: 'outstanding-web', label: 'Outstanding Reports' },
  { id: 'backup-web',      label: 'Auto Backup' },
  { id: 'delete-web',      label: 'Auto Delete' },
];

const WebDevProgressNavigator = () => {
  const [activeSection, setActiveSection] = useState('far-web');
  const [scrollPct, setScrollPct] = useState(0);
  const [visible, setVisible] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('up');
  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    const scrollTop  = window.scrollY;
    setVisible(scrollTop > 200);

    // Track scroll direction
    if (scrollTop > lastScrollY.current) {
      setScrollDirection('down');
    } else if (scrollTop < lastScrollY.current) {
      setScrollDirection('up');
    }
    lastScrollY.current = scrollTop <= 0 ? 0 : scrollTop;

    // Get offsets of all sections in the document
    const offsets = SECTIONS.map(s => {
      const el = document.getElementById(s.id);
      if (el) {
        return el.getBoundingClientRect().top + window.scrollY;
      }
      return 0;
    });

    // Interpolate scootie position between the corresponding checkpoint dots
    let scootyPct = 0;
    const n = SECTIONS.length;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    
    if (scrollTop <= offsets[0]) {
      scootyPct = 0;
    } else if (scrollTop >= offsets[n - 1] || scrollTop >= maxScroll) {
      scootyPct = 100;
    } else {
      for (let i = 0; i < n - 1; i++) {
        const startOffset = offsets[i];
        const endOffset = offsets[i + 1];
        if (scrollTop >= startOffset && scrollTop <= endOffset) {
          const range = endOffset - startOffset;
          const frac = range > 0 ? (scrollTop - startOffset) / range : 0;
          const step = 100 / (n - 1);
          scootyPct = (i + frac) * step;
          break;
        }
      }
    }
    setScrollPct(scootyPct);

    // Determine active section by scroll position
    let current = 'far-web';
    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.45) {
          current = section.id;
        }
      }
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav
      className={`webdev-nav ${visible ? 'webdev-nav--visible' : ''}`}
      aria-label="Web projects section navigator"
    >
      {/* Vertical progress track */}
      <div className="webdev-nav__track">
        <div
          className="webdev-nav__progress"
          style={{ height: `${scrollPct}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Moving Scootie Element */}
      <div
        className={`webdev-nav__scootie ${scrollDirection === 'down' ? 'webdev-nav__scootie--down' : ''}`}
        style={{ top: `${scrollPct}%` }}
        aria-hidden="true"
      >
        <img src="/scootie-scroll-element.png" alt="scootie scroll marker" />
      </div>

      {/* Section dots */}
      <ul className="webdev-nav__dots">
        {SECTIONS.map((s) => (
          <li key={s.id}>
            <button
              className={`webdev-nav__dot ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => scrollTo(s.id)}
              aria-label={`Navigate to ${s.label}`}
              title={s.label}
            >
              <span className="webdev-nav__dot-inner" />
              <span className="webdev-nav__label">{s.label}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Scroll to top */}
      {scrollPct > 80 && (
        <button
          className="webdev-nav__top-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to top"
          title="Back to Top"
        >
          ↑
        </button>
      )}
    </nav>
  );
};

export default WebDevProgressNavigator;
